/**
 * Checking a player's board.
 *
 * Violations are computed against the CLUES, never against the stored solution. That is what lets
 * a player explore a legal-but-wrong line of reasoning and be told the truth about it — the two
 * existing implementations we studied both compared to the answer, which means their players could
 * never think ahead.
 *
 * Runs in the browser as well as on the server: it needs the clues, which are public, but never the
 * solution, which is not.
 */

import { evaluateAtom, evaluateGeneralClue, kindAt, roomAt } from "./atoms";
import { colOf, rowOf } from "./grid";
import type { Atom, Board, CellIdx, Clue, GeneralClue } from "./types";

/** A cell per character, or null where the player has not committed yet. */
export type Placements = ReadonlyArray<CellIdx | null>;

export type ViolationCode =
  | "duplicateRow"
  | "duplicateColumn"
  | "sameCell"
  | "blockedCell"
  | "clue"
  | "crimeSceneCrowded";

export interface Violation {
  code: ViolationCode;
  /** Everyone implicated, so the board can highlight them all. */
  chars: number[];
  cells: CellIdx[];
  /** The clue at fault, when the violation came from one. */
  atom?: Atom;
}

export interface CheckInput {
  board: Board;
  characterCount: number;
  clues: Clue[];
  generalClues: GeneralClue[];
  placements: Placements;
}

const placedEntries = (placements: Placements): Array<[number, CellIdx]> =>
  placements.flatMap((cell, char) => (cell === null ? [] : [[char, cell] as [number, CellIdx]]));

/**
 * Everything wrong with the board as it stands.
 *
 * Only reports what is *definitely* wrong: a clue is flagged when every character it mentions has
 * been placed and the statement is false. Half-finished boards are not nagged at.
 */
export function findViolations(input: CheckInput): Violation[] {
  const { board, clues, generalClues, placements, characterCount } = input;
  const size = board.size;
  const violations: Violation[] = [];
  const placed = placedEntries(placements);

  // Structural rules: one person per row and column, on a cell they can stand on.
  const byRow = new Map<number, number[]>();
  const byCol = new Map<number, number[]>();
  const byCell = new Map<CellIdx, number[]>();

  for (const [char, cell] of placed) {
    if (!board.occupiable[cell]) {
      violations.push({ code: "blockedCell", chars: [char], cells: [cell] });
    }
    const row = rowOf(cell, size);
    const col = colOf(cell, size);
    byRow.set(row, [...(byRow.get(row) ?? []), char]);
    byCol.set(col, [...(byCol.get(col) ?? []), char]);
    byCell.set(cell, [...(byCell.get(cell) ?? []), char]);
  }

  for (const [, chars] of byRow) {
    if (chars.length > 1) {
      violations.push({
        code: "duplicateRow",
        chars,
        cells: chars.map((c) => placements[c] as CellIdx),
      });
    }
  }
  for (const [, chars] of byCol) {
    if (chars.length > 1) {
      violations.push({
        code: "duplicateColumn",
        chars,
        cells: chars.map((c) => placements[c] as CellIdx),
      });
    }
  }
  for (const [cell, chars] of byCell) {
    if (chars.length > 1) violations.push({ code: "sameCell", chars, cells: [cell] });
  }

  // The murder rule: the victim's area holds the victim and one other person, nobody else.
  const victim = characterCount - 1;
  const victimCell = placements[victim];
  if (victimCell !== null && victimCell !== undefined) {
    const scene = roomAt(board, victimCell);
    const inScene = placed.filter(([, cell]) => roomAt(board, cell) === scene);
    if (inScene.length > 2) {
      violations.push({
        code: "crimeSceneCrowded",
        chars: inScene.map(([char]) => char),
        cells: inScene.map(([, cell]) => cell),
      });
    }
  }

  // Clues: only judged once everyone they mention is on the board.
  const isPlaced = (char: number): boolean =>
    placements[char] !== null && placements[char] !== undefined;

  for (const clue of clues) {
    for (const atom of clue.atoms) {
      const involved = charactersIn(atom);
      if (!involved.every(isPlaced)) continue;
      // Atoms about being alone or unique depend on everyone's position, so wait for a full board.
      if (needsFullBoard(atom) && placed.length < characterCount) continue;

      const arrangement = placements.map((cell) => (cell ?? -1) as CellIdx);
      if (!evaluateAtom(atom, board, arrangement)) {
        violations.push({
          code: "clue",
          chars: involved,
          cells: involved.map((c) => placements[c] as CellIdx),
          atom,
        });
      }
    }
  }

  if (placed.length === characterCount) {
    const arrangement = placements.map((cell) => cell as CellIdx);
    for (const clue of generalClues) {
      if (!evaluateGeneralClue(clue, board, arrangement)) {
        violations.push({ code: "clue", chars: [], cells: [] });
      }
    }
  }

  return violations;
}

/** Which characters an atom talks about. */
function charactersIn(atom: Atom): number[] {
  return "other" in atom ? [atom.char, atom.other] : [atom.char];
}

/** Statements that can only be judged once every character is placed. */
function needsFullBoard(atom: Atom): boolean {
  return (
    atom.kind === "alone" ||
    atom.kind === "aloneWith" ||
    atom.kind === "onlyOn" ||
    atom.kind === "onlyBeside" ||
    atom.kind === "otherBeside"
  );
}

/** True when the board is complete and nothing is broken. */
export function isBoardComplete(input: CheckInput): boolean {
  return (
    input.placements.filter((cell) => cell !== null).length === input.characterCount &&
    findViolations(input).length === 0
  );
}

/** Which cells a player should see greyed out, given who is already placed. */
export function blockedCells(board: Board, placements: Placements): Set<CellIdx> {
  const size = board.size;
  const blocked = new Set<CellIdx>();
  for (const [, cell] of placedEntries(placements)) {
    const row = rowOf(cell, size);
    const col = colOf(cell, size);
    for (let i = 0; i < size; i++) {
      blocked.add(row * size + i);
      blocked.add(i * size + col);
    }
  }
  return blocked;
}

/** Where the murderer must be, once the board is solved: the other person in the victim's area. */
export function murdererFrom(board: Board, placements: Placements, characterCount: number): number | null {
  const victim = characterCount - 1;
  const victimCell = placements[victim];
  if (victimCell === null || victimCell === undefined) return null;
  const scene = roomAt(board, victimCell);
  const others = placedEntries(placements).filter(
    ([char, cell]) => char !== victim && roomAt(board, cell) === scene,
  );
  return others.length === 1 ? (others[0]?.[0] ?? null) : null;
}

/** Kept for the object-kind lookup the board renderer needs. */
export { kindAt };
